// architecture.md §4: Custom Element + Shadow DOM (open — DevTools-inspectable, still CSS-
// isolated). One instance = one flow through prd.md §2's state machine.
import type { ScanRequestEmbed } from "@apc/shared-types";
import { createScan, pollScan, submitEmail, type ApiClientConfig, type PollHandle } from "./apiClient.js";
import { dispatchWidgetEvent } from "./events.js";
import { FormRenderer, validateSameDomain, type FormSubmitData } from "./formRenderer.js";
import { renderReport } from "./reportRenderer.js";
import { reduce, type Action } from "./stateMachine.js";
import { getTurnstileToken } from "./turnstile.js";
import { initialWidgetState, type Finding, type UrlProgress, type WidgetState } from "./types.js";
import { WIDGET_STYLES } from "./styles.js";

const TAG_NAME = "adpresso-ad-policy-checker";
const DEFAULT_API_BASE = "https://scan.wpadpresso.com";

export class AdPolicyCheckerElement extends HTMLElement {
  private root: ShadowRoot;
  private body: HTMLElement;
  private state: WidgetState = initialWidgetState();
  private pollHandle: PollHandle | null = null;
  private apiConfig: ApiClientConfig;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = WIDGET_STYLES;
    this.root.appendChild(style);
    this.body = document.createElement("div");
    this.body.className = "apc-body";
    this.root.appendChild(this.body);
    this.apiConfig = { baseUrl: this.getAttribute("data-api-base") ?? DEFAULT_API_BASE };
  }

  connectedCallback(): void {
    const scanIdFromUrl = new URLSearchParams(window.location.search).get("scan");
    if (scanIdFromUrl) {
      this.loadPermalink(scanIdFromUrl);
    } else {
      this.dispatch({ type: "VALIDATION_OK" });
    }
  }

  disconnectedCallback(): void {
    this.pollHandle?.stop();
  }

  private get originTag(): string {
    return this.getAttribute("data-origin-tag") ?? "unknown";
  }

  private get embedContext(): ScanRequestEmbed["context"] {
    return this.getAttribute("data-context") === "gutenberg" ? "gutenberg" : "script-embed";
  }

  private dispatch(action: Action): void {
    this.state = reduce(this.state, action);
    this.render();
  }

  // --- permalink load (prd.md §2.3 S6) ---

  private loadPermalink(scanId: string): void {
    // M3.8: individual report permalinks should not be indexed — set at the host document
    // level (a Shadow DOM style/meta tag has no effect on how crawlers see the page).
    if (!document.querySelector('meta[name="robots"]')) {
      const meta = document.createElement("meta");
      meta.name = "robots";
      meta.content = "noindex";
      document.head.appendChild(meta);
    }
    this.dispatch({ type: "PERMALINK_LOADING" });
    this.startPolling(scanId);
  }

  // --- form submit (prd.md §2.1 S0-S2) ---

  private async handleFormSubmit(data: FormSubmitData): Promise<void> {
    const validationError = validateSameDomain(data.urls.map((u) => u.url));
    if (validationError) {
      this.dispatch({ type: "VALIDATION_ERROR", message: validationError });
      return;
    }
    if (!data.consentChecked) {
      this.dispatch({ type: "VALIDATION_ERROR", message: "Please confirm you're authorized to scan this domain." });
      return;
    }

    this.dispatch({ type: "SUBMIT" });

    const siteKey = this.getAttribute("data-turnstile-sitekey");
    let turnstileToken: string;
    try {
      const container = document.createElement("div");
      turnstileToken = await getTurnstileToken(container, siteKey);
    } catch {
      this.dispatch({ type: "SUBMIT_ERROR", message: "The security check failed to load. Please try again." });
      return;
    }

    const result = await createScan(this.apiConfig, {
      urls: data.urls.map((u) => ({ url: u.url, role: u.role, source: "user" as const })),
      consentCheckbox: true,
      turnstileToken,
      embed: { originTag: this.originTag, context: this.embedContext, hostUrl: window.location.href },
      emailOptIn: null,
    });

    if (!result.ok) {
      const message =
        result.error.code === "RATE_LIMIT_EXCEEDED"
          ? "You've reached the scan limit for now — please try again later."
          : result.error.code === "DOMAIN_MISMATCH"
            ? "These URLs don't all belong to the same domain."
            : "Something went wrong submitting the scan. Please try again.";
      this.dispatch({ type: "SUBMIT_ERROR", message }); // F1
      dispatchWidgetEvent(this, "adpresso:error", { scanId: null, code: result.error.code, message });
      return;
    }

    const urls: UrlProgress[] = result.data.urls.map((u) => ({
      urlId: u.urlId,
      url: u.url,
      status: u.status,
      errorCode: null,
      partial: false,
      score: null,
    }));
    this.dispatch({ type: "SCAN_CREATED", scanId: result.data.scanId, domain: result.data.domain, urls });
    dispatchWidgetEvent(this, "adpresso:scan-started", {
      scanId: result.data.scanId,
      domain: result.data.domain,
      originTag: this.originTag,
    });

    // S3: permalink survives reload/close (prd.md §2.2).
    const url = new URL(window.location.href);
    url.searchParams.set("scan", result.data.scanId);
    window.history.pushState({}, "", url.toString());

    this.startPolling(result.data.scanId);
  }

  private startPolling(scanId: string): void {
    this.pollHandle?.stop();
    this.pollHandle = pollScan(
      this.apiConfig,
      scanId,
      (data) => {
        const urls: UrlProgress[] = data.urls.map((u) => ({
          urlId: u.urlId,
          url: this.state.main.kind === "scanning" || this.state.main.kind === "report"
            ? (this.state.main.urls.find((existing) => existing.urlId === u.urlId)?.url ?? "")
            : "",
          status: u.status,
          errorCode: u.errorCode ?? null,
          partial: u.partial ?? false,
          score: u.score ?? null,
        }));
        const findings = data.events.filter((e) => e.type === "finding").map((e) => e.payload as Finding);

        dispatchWidgetEvent(this, "adpresso:scan-progress", {
          scanId,
          urlsDone: urls.filter((u) => u.status !== "queued" && u.status !== "running").length,
          urlsTotal: urls.length,
        });

        this.dispatch({
          type: "POLL_UPDATE",
          scanId,
          status: data.status,
          urls,
          domain: data.domain,
          domainScore: data.score?.domainScore ?? null,
          findings,
        });

        if (data.status === "complete" || data.status === "partial") {
          dispatchWidgetEvent(this, "adpresso:scan-complete", {
            scanId,
            domainScore: data.score?.domainScore ?? null,
            officialViolationCount: data.score?.officialViolationCount ?? 0,
          });
        }
      },
      (error) => {
        if (error.code === "SCAN_NOT_FOUND") {
          this.dispatch({ type: "PERMALINK_NOT_FOUND" });
        } else {
          dispatchWidgetEvent(this, "adpresso:error", { scanId, code: error.code, message: error.message ?? "" });
        }
      },
    );
  }

  // --- email gate (prd.md §2.3 S5/S5a/S5b) ---

  private async handleEmailSubmit(email: string, scanId: string, trigger: "during_scan" | "post_report"): Promise<void> {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.dispatch({ type: "EMAIL_ERROR", message: "Please enter a valid email address." });
      return;
    }
    this.dispatch({ type: "EMAIL_SUBMIT" });
    const result = await submitEmail(this.apiConfig, scanId, email, trigger);
    if (!result.ok) {
      this.dispatch({ type: "EMAIL_ERROR", message: "Couldn't send that right now — the report itself is still visible above." }); // S5b, non-blocking
      return;
    }
    this.dispatch({ type: "EMAIL_SUCCESS" });
    dispatchWidgetEvent(this, "adpresso:email-submitted", { scanId, trigger });
  }

  // --- rendering ---

  render(): void {
    const { main } = this.state;

    switch (main.kind) {
      case "form": {
        const form = new FormRenderer(this.body, { onSubmit: (data) => void this.handleFormSubmit(data) });
        form.render(main.validationError);
        return;
      }
      case "submitting":
        this.body.innerHTML = `<p class="apc-loading">Starting your scan…</p>`;
        return;
      case "submit_error": // F1
        this.body.innerHTML = `
          <p class="apc-error-message" role="alert">${main.message}</p>
          <button class="apc-cta-button apc-retry-button">Try again</button>
        `;
        this.body.querySelector(".apc-retry-button")?.addEventListener("click", () => this.dispatch({ type: "RETRY_TO_FORM" }));
        return;
      case "scanning": // S3/S3a-e
        this.renderScanning(main.urls, main.scanId);
        return;
      case "all_failed": // F4
        this.body.innerHTML = `
          <div class="apc-empty-state">
            <h3>The scan couldn't be completed</h3>
            <ul class="apc-progress-list">
              ${main.urls.map((u) => `<li class="apc-progress-item"><span class="apc-status-badge apc-status-${u.status}">${u.status}</span>${u.url}</li>`).join("")}
            </ul>
            <button class="apc-cta-button apc-retry-button">Try again</button>
          </div>
        `;
        this.body.querySelector(".apc-retry-button")?.addEventListener("click", () => this.dispatch({ type: "RESCAN" }));
        return;
      case "report": // S4
        this.body.innerHTML =
          renderReport(main.domain, main.domainScore, main.findings) +
          `<button class="apc-cta-button apc-email-cta">Email me the PDF report</button>`;
        this.body
          .querySelector(".apc-email-cta")
          ?.addEventListener("click", () => this.dispatch({ type: "EMAIL_GATE_OPEN", trigger: "post_report" }));
        this.renderEmailGate(main.scanId);
        return;
      case "permalink_not_found": // S6, unknown/expired
        this.body.innerHTML = `
          <div class="apc-empty-state">
            <h3>We couldn't find that report</h3>
            <p>It may have expired, or the link might be incomplete.</p>
            <button class="apc-cta-button apc-retry-button">Start a new scan</button>
          </div>
        `;
        this.body.querySelector(".apc-retry-button")?.addEventListener("click", () => this.dispatch({ type: "RESCAN" }));
        return;
    }
  }

  private renderScanning(urls: UrlProgress[], scanId: string): void {
    const doneCount = urls.filter((u) => u.status !== "queued" && u.status !== "running").length;
    this.body.innerHTML = `
      <p class="apc-loading">Checking page ${Math.min(doneCount + 1, urls.length)} of ${urls.length}…</p>
      <ul class="apc-progress-list">
        ${urls
          .map(
            (u) => `<li class="apc-progress-item"><span class="apc-status-badge apc-status-${u.status}">${u.status.replace(/_/g, " ")}</span>${u.url}</li>`,
          )
          .join("")}
      </ul>
      <button class="apc-cta-button apc-email-during-scan">Email me the report instead of waiting</button>
    `;
    this.body
      .querySelector(".apc-email-during-scan")
      ?.addEventListener("click", () => this.dispatch({ type: "EMAIL_GATE_OPEN", trigger: "during_scan" }));
    this.renderEmailGate(scanId);
  }

  private renderEmailGate(scanId: string): void {
    const { emailGate } = this.state;
    if (emailGate.status === "closed") return;

    const gateEl = document.createElement("div");
    gateEl.className = "apc-email-gate";

    if (emailGate.status === "success") {
      gateEl.innerHTML = `<p>${emailGate.trigger === "during_scan" ? "We'll email you as soon as the scan finishes." : "The PDF is on its way."}</p>`;
    } else {
      gateEl.innerHTML = `
        <input type="email" class="apc-email-input" placeholder="you@example.com" />
        <button class="apc-cta-button apc-email-submit-button" ${emailGate.status === "submitting" ? "disabled" : ""}>
          ${emailGate.status === "submitting" ? "Sending…" : "Send"}
        </button>
        ${emailGate.status === "error" ? `<p class="apc-error-message" role="alert">${emailGate.errorMessage}</p>` : ""}
      `;
      gateEl.querySelector(".apc-email-submit-button")?.addEventListener("click", () => {
        const email = gateEl.querySelector<HTMLInputElement>(".apc-email-input")!.value.trim();
        void this.handleEmailSubmit(email, scanId, emailGate.trigger ?? "post_report");
      });
    }
    this.body.appendChild(gateEl);
  }
}

export function defineAdPolicyCheckerElement(): void {
  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, AdPolicyCheckerElement);
  }
}

export { TAG_NAME };
